import { forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, type AlertLive } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button';
import { useTranslation } from '../../i18n/useTranslation';
import { aquariumSettingsPath } from '../../utils/supplySystem';

export interface ZeovitUnavailableNoticeProps {
  aquariumId: string;
  /** Announcement politeness (Alert default for info: polite). */
  live?: AlertLive;
  /** Set to -1 when focus is moved to the notice (e.g. after a refused submit). */
  tabIndex?: number;
}

/**
 * Info notice shown instead of the ZeoVit module for an aquarium whose supply system
 * is not ZeoVit (#1784) — on direct URL access and after a 409 SUPPLY_SYSTEM_NOT_ZEOVIT.
 * Its action opens the aquarium's edit form, where the supply system is changed.
 */
export const ZeovitUnavailableNotice = forwardRef<HTMLDivElement, ZeovitUnavailableNoticeProps>(
  function ZeovitUnavailableNotice({ aquariumId, live, tabIndex }, ref) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
      <Alert
        ref={ref}
        tabIndex={tabIndex}
        tone="info"
        live={live}
        title={t('zeovit.unavailable.title')}
        data-testid="zeovit-unavailable-notice"
        action={
          <Button variant="secondary" onClick={() => navigate(aquariumSettingsPath(aquariumId))}>
            {t('zeovit.unavailable.openSettings')}
          </Button>
        }
      >
        {t('zeovit.unavailable.message')}
      </Alert>
    );
  },
);

ZeovitUnavailableNotice.displayName = 'ZeovitUnavailableNotice';

export default ZeovitUnavailableNotice;
